import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { AppProvider, Page, Card, DescriptionList, Badge, Layout, Button, ButtonGroup, TextField, Toast, Frame } from '@shopify/polaris';

import axiosRails from '@utils/axios-rails';
import { ta } from '@utils/index';
import { valueToBadgeStatus } from '@utils/columns';
import EvidenceList from 'components/shared/EvidenceList';
import GeneralModal from 'components/shared/GeneralModal';

const propTypes = {
  record: PropTypes.object.isRequired,
  evidences: PropTypes.array,
  updateURL: PropTypes.string,
  breadcrumbs: PropTypes.array.isRequired,
}

const defaultProps = {
  evidences: [],
}

const ResourceTransferShow = (props) => {
  const [record, setRecord] = useState(props.record);
  const [toast, setToast] = useState(null);
  const [rejectNotes, setRejectNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const rejectModalRef = React.useRef(null);

  // Same endpoint the index uses; resolving from here keeps the decision next to the evidence.
  const resolveTransfer = async (status, notes = null) => {
    setLoading(true);
    try {
      const itemURL = props.updateURL.replace(":id", record.id);
      const payload = { resource_transfer: notes === null ? { status } : { status, notes } };
      const response = await axiosRails.put(itemURL, payload);

      if (response.data.success) {
        setRecord({ ...record, ...response.data.record });
        setToast(status === "accepted" ? "Traslado aceptado" : "Traslado rechazado");
        rejectModalRef.current?.close();
      } else {
        setToast("Error al actualizar el traslado");
      }
    } catch (error) {
      setToast(I18n.t("messages.request_error"));
    }
    setLoading(false);
  };

  const handleAccept = () => {
    if (confirm("¿Está seguro de aceptar el traslado?")) resolveTransfer("accepted");
  };

  return (
    <AppProvider>
      <Frame>
      <Page 
        title={ I18n.t("activerecord.models.resource_transfer", { count: 1 }) }
        subtitle={(
          <Badge status={ valueToBadgeStatus[record.status] } >
            {I18n.t(`enum.status.${record.status}`)}
          </Badge>
        )}
        breadcrumbs={ props.breadcrumbs }
      >
        <Layout>
          <Layout.Section>
            <Card title="Información General" sectioned>
              <DescriptionList
                items={[
                  { term: ta("general.type"), description: I18n.t(`enum.polymorphic_type.${record.resource_type}`) },
                  { term: ta("resource_transfer.transfer_type"), description: I18n.t(`enum.transfer_type.${record.transfer_type}`) },
                  { term: ta("resource_transfer.created_at"), description: I18n.l("time.formats.date_only", record.created_at) },
                  { term: ta("resource_transfer.requested_by"), description: record.requested_by.name },
                  { term: ta("resource_transfer.resolved_by"), description: record.resolved_by && record.resolved_by.name },
                  { term: ta("general.notes"), description: record.notes },
                ]}
              />
            </Card>

            <EvidenceList evidences={ props.evidences } title="Evidencias del mensajero" />

            { record.status === "pending" && !!props.updateURL &&
              <Card title="Resolver el traslado" sectioned>
                <p className="mb12">Al aceptar, el producto queda registrado como recibido.</p>
                <ButtonGroup>
                  <Button primary loading={ loading } onClick={ handleAccept }>Aceptar</Button>
                  <Button destructive disabled={ loading } onClick={ () => { setRejectNotes(""); rejectModalRef.current?.open(); } }>
                    Rechazar
                  </Button>
                </ButtonGroup>
              </Card>
            }
          </Layout.Section>

          <Layout.Section secondary>
            <Card title="Información Orden" sectioned>
              <DescriptionList
                items={[
                  { term: ta("general.id"), description: record.resource.provider_id },
                  { term: ta("dispatch_order.order_number"), description: record.resource.order_number },
                  { term: I18n.t("activerecord.models.team", { count: 1 }), description: record.team_name },
                ]}
              />
            </Card>
          </Layout.Section>
        </Layout>

        <GeneralModal
          ref={ rejectModalRef }
          title="Rechazar traslado"
          primaryAction={{
            content: "Rechazar",
            onAction: () => resolveTransfer("rejected", rejectNotes),
            loading: loading,
            disabled: !rejectNotes,
          }}
          secondaryActions={[{ content: "Cancelar", onAction: () => rejectModalRef.current?.close(), loading: false, disabled: false }]}
        >
          <TextField
            label="Motivo del rechazo"
            value={ rejectNotes }
            onChange={ setRejectNotes }
            multiline={ 3 }
            autoComplete="off"
          />
        </GeneralModal>

        { !!toast && <Toast content={ toast } onDismiss={ () => setToast(null) } /> }
      </Page>
      </Frame>
    </AppProvider>
  )
};

ResourceTransferShow.propTypes = propTypes;
ResourceTransferShow.defaultProps = defaultProps;

export default ResourceTransferShow;